import { useCallback, useMemo, useRef, useState } from 'react';
import { BiDotsVerticalRounded, BiExport, BiPlus, BiReceipt } from 'react-icons/bi';
import { CgImport } from 'react-icons/cg';
import { FaFileCsv, FaFilePdf } from 'react-icons/fa6';
import { FiEdit2, FiTag, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { ConfirmationModal } from '@/components';
import { ButtonPill, ButtonPillPrimary, ButtonSmall } from '@/components/Button';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { EmptyPage } from '@/components/EmptyPage';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { useImportedTransactionsStore } from '@/store/expenses-store/transactions/imported-transactions.store.ts';
import type {
  Transaction,
  TransactionRow,
} from '@/store/expenses-store/transactions/transactions.dto.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { ExpensesToolbar, type FilterGroup } from '@pages/Expenses/components/ExpensesToolbar.tsx';
import { ManageCategoriesModal } from '@pages/Expenses/components/modals/ManageCategoriesModal.tsx';
import TransactionModal from '@pages/Expenses/components/modals/TransactionModal';
import CardSkeleton from '@pages/Expenses/components/Skeletons/CardSkeleton.tsx';
import {
  AMOUNT_OPTIONS,
  DATE_OPTIONS,
  DEFAULT_FILTERS,
  matchesDate,
  SEGMENT_EMPTY_STATE,
} from '@pages/Expenses/TransactionsPage/transaction.constants.ts';
import type {
  TransactionSegment,
  TransactionsProps,
} from '@pages/Expenses/TransactionsPage/transactions.types.ts';
import {
  amountStyle,
  formatSignedAmount,
  isInflow,
} from '@pages/Expenses/TransactionsPage/utils/transactionColors';
import {
  transactionStatusLabel,
  transactionStatusTone,
  transactionStatusVariant,
} from '@pages/Expenses/TransactionsPage/utils/transactionStatus';
import { recipientName } from '@pages/Expenses/TransactionsPage/utils/transformExpense';
import { useCategoryFilterParam } from '@pages/Expenses/TransactionsPage/utils/useCategoryFilterParam';
import { useTransactionApprovals } from '@pages/Expenses/TransactionsPage/utils/useTransactionApprovals.ts';

import styles from './MobileTransactions.module.scss';

const matchesAmount = (amount: number, filter: string) => {
  if (filter === 'under-1000') return amount < 1000;
  if (filter === '1000-5000') return amount >= 1000 && amount <= 5000;
  if (filter === 'over-5000') return amount > 5000;
  return true;
};

export const MobileTransactions = ({
  data,
  loading,
  isCsvImporting,
  error,
  canManage,
  canManageCategories,
}: TransactionsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [startInEdit, setStartInEdit] = useState(false);
  const [segment, setSegment] = useState<TransactionSegment>('all');
  const [filters, setFilters] = useState<Record<string, string>>(DEFAULT_FILTERS);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [exportButtonEl, setExportButtonEl] = useState<HTMLButtonElement | null>(null);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<TransactionRow | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const {
    transactions,
    transactionCategories,
    exportTransactions,
    exportLoading,
    removeTransaction,
    getAllTransactions,
  } = useTransactionsStore();
  const { segments } = useTransactionApprovals();
  const importTransactionFile = useImportedTransactionsStore((s) => s.importTransactionFile);
  const importLoading = useImportedTransactionsStore((s) => s.importLoading);

  const applyCategoryFilter = useCallback(
    (categoryTitle: string) => setFilters((prev) => ({ ...prev, category: categoryTitle })),
    [],
  );

  useCategoryFilterParam(transactionCategories, applyCategoryFilter);

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      { key: 'date', title: 'Date', options: DATE_OPTIONS },
      {
        key: 'category',
        title: 'Category',
        options: [
          { value: 'all', label: 'All' },
          ...transactionCategories.map((category) => ({
            value: category.title,
            label: category.title,
          })),
        ],
      },
      { key: 'amount', title: 'Amount', options: AMOUNT_OPTIONS },
    ],
    [transactionCategories],
  );

  const filteredData = useMemo(
    () =>
      data.filter((row) => {
        const inflow = isInflow(row.transactionType);
        if (segment === 'approvals' && row.status !== 'needs_approval') return false;
        if (segment === 'payments' && inflow) return false;
        if (segment === 'refunds' && !inflow) return false;
        if (!matchesDate(row.date, filters.date)) return false;
        if (filters.category !== 'all' && row.type !== filters.category) return false;
        if (!matchesAmount(row.amount, filters.amount)) return false;
        return true;
      }),
    [data, segment, filters],
  );

  const emptyState = SEGMENT_EMPTY_STATE[segment];

  const openTransaction = (id: string, edit = false) => {
    const transaction = transactions.find((t) => t.id === id);
    if (!transaction) return;
    setSelectedTransaction(transaction);
    setStartInEdit(edit);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleSegmentChange = (value: TransactionSegment) => {
    setSegment(value);
    setFilters(DEFAULT_FILTERS);
  };

  const handleAddTransaction = () => {
    setSelectedTransaction(null);
    setStartInEdit(false);
    setIsModalOpen(true);
  };

  const handleImportFile = async (file: File | undefined) => {
    if (!file) return;
    const isSuccess = await importTransactionFile(file);
    if (isSuccess) navigate('/manage/transactions/imported');
  };

  const menuRow = filteredData.find((row) => row.id === menuRowId) ?? null;

  const closeMenu = () => {
    setMenuRowId(null);
    setMenuAnchorEl(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <ExpensesToolbar
          segments={segments}
          segment={segment}
          onSegmentChange={handleSegmentChange}
          filterGroups={filterGroups}
          filterValues={filters}
          onFilterChange={(groupKey, value) =>
            setFilters((prev) => ({ ...prev, [groupKey]: value }))
          }
          onClearFilters={() => setFilters(DEFAULT_FILTERS)}
          actions={
            <>
              <ButtonPill
                ref={setExportButtonEl}
                disabled={exportLoading}
                aria-haspopup="menu"
                aria-expanded={isExportMenuOpen}
                onClick={() => setIsExportMenuOpen((prev) => !prev)}
              >
                <BiExport aria-hidden="true" /> {exportLoading ? 'Exporting…' : 'Export'}
              </ButtonPill>
              {canManageCategories && (
                <ButtonPill onClick={() => setIsCategoriesModalOpen(true)}>
                  <FiTag aria-hidden="true" /> Categories
                </ButtonPill>
              )}
              {canManage && (
                <>
                  <ButtonPill
                    disabled={isCsvImporting || importLoading}
                    onClick={() => importInputRef.current?.click()}
                  >
                    <CgImport aria-hidden="true" />{' '}
                    {isCsvImporting || importLoading ? 'Importing…' : 'Import'}
                  </ButtonPill>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,application/pdf"
                    hidden
                    onChange={(event) => {
                      void handleImportFile(event.target.files?.[0]);
                      event.target.value = '';
                    }}
                  />
                  <ButtonPillPrimary onClick={handleAddTransaction}>
                    <BiPlus aria-hidden="true" /> Add
                  </ButtonPillPrimary>
                </>
              )}
            </>
          }
        />
      </div>

      <div className={styles.content}>
        {loading && (
          <div className={styles.cards}>
            {[1, 2, 3, 4, 5].map((index) => (
              <CardSkeleton key={index} height={96} />
            ))}
          </div>
        )}
        {error && <p className={styles.error}>{error}</p>}

        {!loading &&
          !error &&
          (filteredData.length === 0 ? (
            <EmptyPage
              icon={<BiReceipt size={64} />}
              title={emptyState.title}
              desc={emptyState.desc}
              action={
                canManage && emptyState.canAdd ? (
                  <ButtonSmall onClick={handleAddTransaction}>Add</ButtonSmall>
                ) : undefined
              }
            />
          ) : (
            <div className={styles.cards}>
              {filteredData.map((row) => {
                const pending = row.status === 'pending';
                return (
                  <div
                    key={row.id}
                    className={`${styles.card} ${styles.cardClickable} ${
                      pending ? styles.pendingRow : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openTransaction(row.id)}
                    onKeyDown={(event) => event.key === 'Enter' && openTransaction(row.id)}
                  >
                    <div className={styles.cardMain}>
                      <span className={styles.cardTitle}>{row.date}</span>
                      <span className={styles.cardMeta}>
                        <span
                          className={`${styles.cardMetaPart} ${styles.cardDescription}`}
                          title={row.description || undefined}
                        >
                          {row.description || '—'}
                        </span>
                        <span aria-hidden="true">&nbsp;· </span>
                        {recipientName(row) && (
                          <>
                            <span className={styles.cardMetaPart}>{recipientName(row)}</span>
                            <span aria-hidden="true">&nbsp;· </span>
                          </>
                        )}
                        <span className={styles.cardMetaPart}>{row.type}</span>
                        <span aria-hidden="true">&nbsp;· </span>
                      </span>
                      <span className={styles.cardMetaPart}>
                        <StatusPill
                          label={transactionStatusLabel(row.status)}
                          tone={transactionStatusTone(row.status)}
                          variant={transactionStatusVariant(row.status)}
                        />
                      </span>
                    </div>

                    <div className={styles.cardSide}>
                      <span style={amountStyle(row.transactionType)}>
                        {formatSignedAmount(row.amount, row.transactionType)}
                      </span>
                      {canManage && row.method === 'manual' && (
                        <button
                          type="button"
                          className={styles.overflow}
                          aria-label="Row actions"
                          aria-haspopup="menu"
                          aria-expanded={menuRowId === row.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            setMenuRowId(row.id);
                            setMenuAnchorEl(event.currentTarget);
                          }}
                        >
                          <BiDotsVerticalRounded />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
      </div>

      <ContextMenu
        isOpen={isExportMenuOpen}
        onClose={() => setIsExportMenuOpen(false)}
        anchorEl={exportButtonEl}
        align="center"
        items={[
          {
            label: 'Export PDF',
            icon: <FaFilePdf />,
            onClick: () => void exportTransactions('pdf'),
          },
          {
            label: 'Export CSV',
            icon: <FaFileCsv />,
            onClick: () => void exportTransactions('csv'),
          },
        ]}
      />

      <ContextMenu
        isOpen={menuRowId !== null}
        onClose={closeMenu}
        anchorEl={menuAnchorEl}
        items={
          menuRow
            ? [
                {
                  label: 'Edit',
                  icon: <FiEdit2 size={14} />,
                  onClick: () => openTransaction(menuRow.id, true),
                },
                {
                  label: 'Delete',
                  icon: <FiTrash2 size={14} />,
                  variant: 'danger' as const,
                  onClick: () => setPendingDeleteRow(menuRow),
                },
              ]
            : []
        }
      />

      <ConfirmationModal
        open={pendingDeleteRow !== null}
        onClose={() => setPendingDeleteRow(null)}
        onConfirm={() => {
          if (pendingDeleteRow) void removeTransaction(pendingDeleteRow.id);
        }}
        title="Delete Transaction"
        message={
          pendingDeleteRow
            ? pendingDeleteRow.reason
              ? `Would you like to delete the transaction from ${pendingDeleteRow.date}, “${pendingDeleteRow.reason}” (${formatSignedAmount(pendingDeleteRow.amount, pendingDeleteRow.transactionType)})? This can’t be undone.`
              : `Would you like to delete the transaction from ${pendingDeleteRow.date} (${formatSignedAmount(pendingDeleteRow.amount, pendingDeleteRow.transactionType)})? This can’t be undone.`
            : ''
        }
      />

      <ManageCategoriesModal
        open={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        onChanged={getAllTransactions}
      />

      <TransactionModal
        key={`${selectedTransaction?.id ?? 'new'}-${startInEdit}`}
        startInEdit={startInEdit}
        open={isModalOpen}
        onClose={handleCloseModal}
        expense={selectedTransaction}
        isExist={!!selectedTransaction}
        canManage={canManage}
      />
    </div>
  );
};
